import { useCallback, useEffect, useRef, useState } from "react";
import { holdMic } from "../lib/micGuard.js";

/**
 * useInterimTranscript — live speech-to-text preview via the browser's Web Speech
 * API (SpeechRecognition). Shows words AS the person speaks, for reassurance and
 * early error-catching.
 *
 * IMPORTANT: this is a *preview only*. It runs alongside the real MediaRecorder
 * capture; the authoritative transcript still comes from the server transcribing
 * the uploaded audio. The interim text is never submitted as the answer — it just
 * reassures the speaker they're being heard. If SpeechRecognition is unavailable
 * (e.g. Firefox), everything degrades to the existing record→upload→transcribe
 * flow with no preview and no regression.
 *
 * Returns:
 *   supported  — boolean, Web Speech API available
 *   interim    — the current live transcript string (interim + finalized so far)
 *   start()    — begin listening
 *   stop()     — stop listening
 *   reset()    — clear the interim text
 */
export function useInterimTranscript() {
  const SR = typeof window !== "undefined"
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;
  const supported = !!SR;

  const [interim, setInterim] = useState("");
  const [final, setFinal] = useState("");     // finalized segments only (Oct 16)
  const recognitionRef = useRef(null);
  const finalizedRef   = useRef("");   // accumulates finalized (non-interim) segments

  const releaseRef = useRef(null);
  const capRef = useRef(null);
  const stop = useCallback(() => {
    clearTimeout(capRef.current);
    const r = recognitionRef.current;
    if (r) {
      // abort(), not stop(): stop() lets the engine finish and can keep the
      // mic for seconds; abort() drops it now.
      try { r.onend = null; r.onresult = null; r.abort(); } catch (_) { /* already stopped */ }
      recognitionRef.current = null;
    }
    releaseRef.current?.(); releaseRef.current = null;
  }, []);

  const reset = useCallback(() => {
    finalizedRef.current = "";
    setInterim("");
    setFinal("");
  }, []);

  const start = useCallback(() => {
    if (!supported) return;
    // Fresh session each time.
    finalizedRef.current = "";
    setInterim("");

    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const text = res[0]?.transcript || "";
        if (res.isFinal) {
          finalizedRef.current = (finalizedRef.current + " " + text).trim();
          setFinal(finalizedRef.current);
        } else {
          interimChunk += text;
        }
      }
      const combined = (finalizedRef.current + " " + interimChunk).trim();
      setInterim(combined);
    };

    // Keep listening across brief pauses: if it ends while we still want it,
    // it will be restarted by the caller stopping explicitly. We null onend in
    // stop() to prevent auto-restart loops.
    recognition.onerror = () => { /* non-fatal: preview just stops updating */ };

    recognitionRef.current = recognition;
    try { recognition.start(); } catch (_) { /* start called twice — ignore */ }
    // Released when the page is hidden, and after 60 s regardless — a field
    // with a mic is for one line, not a standing open microphone.
    releaseRef.current = holdMic(stop);
    clearTimeout(capRef.current);
    capRef.current = setTimeout(stop, 60 * 1000);
  }, [SR, supported, stop]);

  useEffect(() => () => stop(), [stop]);

  return { supported, interim, final, start, stop, reset };
}
